import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface RateLimitState {
  isLocked: boolean;
  remainingAttempts: number;
  lockoutEndTime: number | null;
  remainingSeconds: number;
}

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const TRACKING_WINDOW_MS = 30 * 60 * 1000; // 30 minutes
const STORAGE_KEY = 'login_rate_limit';

interface StoredRateLimitData {
  attempts: number[];
  lockoutEndTime: number | null;
}

function getStoredData(): StoredRateLimitData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Invalid data, reset
  }
  return { attempts: [], lockoutEndTime: null };
}

function setStoredData(data: StoredRateLimitData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function cleanOldAttempts(attempts: number[]): number[] {
  const cutoff = Date.now() - TRACKING_WINDOW_MS;
  return attempts.filter(timestamp => timestamp > cutoff);
}

export function useLoginRateLimit() {
  const [state, setState] = useState<RateLimitState>(() => {
    const data = getStoredData();
    const now = Date.now();
    const cleanedAttempts = cleanOldAttempts(data.attempts);
    
    // Check if lockout is still active
    const isLocked = data.lockoutEndTime !== null && data.lockoutEndTime > now;
    const remainingSeconds = isLocked && data.lockoutEndTime 
      ? Math.ceil((data.lockoutEndTime - now) / 1000) 
      : 0;
    
    return {
      isLocked,
      remainingAttempts: MAX_ATTEMPTS - cleanedAttempts.length,
      lockoutEndTime: isLocked ? data.lockoutEndTime : null,
      remainingSeconds,
    };
  });

  // Countdown timer for lockout
  useEffect(() => {
    if (!state.isLocked || !state.lockoutEndTime) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.ceil((state.lockoutEndTime! - now) / 1000);
      
      if (remaining <= 0) {
        // Lockout expired - reset
        const data = getStoredData();
        setStoredData({ attempts: [], lockoutEndTime: null });
        setState({
          isLocked: false,
          remainingAttempts: MAX_ATTEMPTS,
          lockoutEndTime: null,
          remainingSeconds: 0,
        });
      } else {
        setState(prev => ({ ...prev, remainingSeconds: remaining }));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [state.isLocked, state.lockoutEndTime]);

  const recordFailedAttempt = useCallback(async (email: string) => {
    const now = Date.now();
    const data = getStoredData();
    const cleanedAttempts = cleanOldAttempts(data.attempts);
    
    // Add new attempt
    cleanedAttempts.push(now);
    
    // Check if we've exceeded the limit
    if (cleanedAttempts.length >= MAX_ATTEMPTS) {
      const lockoutEndTime = now + LOCKOUT_DURATION_MS;
      setStoredData({ attempts: cleanedAttempts, lockoutEndTime });
      setState({
        isLocked: true,
        remainingAttempts: 0,
        lockoutEndTime,
        remainingSeconds: Math.ceil(LOCKOUT_DURATION_MS / 1000),
      });
    } else {
      setStoredData({ attempts: cleanedAttempts, lockoutEndTime: null });
      setState({
        isLocked: false,
        remainingAttempts: MAX_ATTEMPTS - cleanedAttempts.length,
        lockoutEndTime: null,
        remainingSeconds: 0,
      });
    }

    // Track in database (fire and forget - don't block login flow)
    try {
      await supabase.from('login_attempts').insert({
        email: email.toLowerCase().trim(),
        success: false,
      });
    } catch (e) {
      console.warn('Failed to record login attempt to database:', e);
    }
  }, []);

  const recordSuccessfulLogin = useCallback(async (email: string) => {
    // Reset local rate limit on successful login
    setStoredData({ attempts: [], lockoutEndTime: null });
    setState({
      isLocked: false,
      remainingAttempts: MAX_ATTEMPTS,
      lockoutEndTime: null,
      remainingSeconds: 0,
    });

    // Track successful login in database
    try {
      await supabase.from('login_attempts').insert({
        email: email.toLowerCase().trim(),
        success: true,
      });
    } catch (e) {
      console.warn('Failed to record successful login to database:', e);
    }
  }, []);

  const canAttemptLogin = useCallback((): boolean => {
    return !state.isLocked;
  }, [state.isLocked]);

  const formatRemainingTime = useCallback((): string => {
    const minutes = Math.floor(state.remainingSeconds / 60);
    const seconds = state.remainingSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [state.remainingSeconds]);

  return {
    isLocked: state.isLocked,
    remainingAttempts: state.remainingAttempts,
    remainingSeconds: state.remainingSeconds,
    recordFailedAttempt,
    recordSuccessfulLogin,
    canAttemptLogin,
    formatRemainingTime,
  };
}
